import { Link } from "react-router-dom";
import { useFetch } from "@/lib/useFetch";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeftRight, ArrowUpRight, Link2 } from "lucide-react";
import { cn } from "@/lib/utils";

const ESTADO_LABEL = {
  estimacion: "Estimación",
  confirmado_pendiente_po: "Confirmado pendiente de PO",
  para_facturar: "Para facturar",
  facturado: "Facturado",
  confirmado_cobro_amex: "Confirmado cobro Amex",
  cobrado: "Cobrado cliente",
  recibido_cobro_cuenta: "Recibido cobro en cuenta",
  pagada_factura: "Pagada factura vinculada",
};
const BADGE = {
  estimacion: "bg-slate-100 text-slate-600 border-slate-200",
  confirmado_pendiente_po: "bg-amber-50 text-amber-700 border-amber-200",
  para_facturar: "bg-blue-50 text-blue-700 border-blue-200",
  facturado: "bg-violet-50 text-violet-700 border-violet-200",
  confirmado_cobro_amex: "bg-indigo-50 text-indigo-700 border-indigo-200",
  cobrado: "bg-emerald-50 text-emerald-700 border-emerald-200",
  recibido_cobro_cuenta: "bg-green-50 text-green-700 border-green-200",
  pagada_factura: "bg-teal-50 text-teal-700 border-teal-200",
};
const fmtEur = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n || 0);

export default function ProjectExternalPayments({ projectId }) {
  const { data: rows, loading } = useFetch("/external-payments", { project_id: projectId });
  const list = rows || [];
  const totProv = list.reduce((a, r) => a + (r.importe_proveedor || 0), 0);
  const totPres = list.reduce((a, r) => a + (r.importe_cliente || 0), 0);

  return (
    <div className="space-y-4" data-testid="project-extpay">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <ArrowLeftRight className="h-4 w-4 text-primary" />
          Pagos gestionados por cuenta del cliente (margen +3,5%) enlazados a este proyecto.
        </div>
        <Button asChild variant="outline" size="sm" className="gap-1.5" data-testid="project-extpay-open">
          <Link to="/pagos-externos">Abrir módulo <ArrowUpRight className="h-3.5 w-3.5" /></Link>
        </Button>
      </div>

      {loading ? (
        <div className="py-10 text-center"><div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin mx-auto" /></div>
      ) : list.length === 0 ? (
        <Card className="border-border border-dashed shadow-none py-12 text-center">
          <p className="text-sm text-muted-foreground">Este proyecto no tiene pagos externos.</p>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="border-border shadow-none p-4"><div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Conceptos</div><div className="font-heading text-2xl font-bold mt-1.5">{list.length}</div></Card>
            <Card className="border-border shadow-none p-4"><div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Importe proveedor</div><div className="font-heading text-2xl font-bold mt-1.5 tabular-nums">{fmtEur(totProv)}</div></Card>
            <Card className="border-border shadow-none p-4"><div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Presupuestado</div><div className="font-heading text-2xl font-bold mt-1.5 tabular-nums text-primary">{fmtEur(totPres)}</div></Card>
            <Card className="border-border shadow-none p-4"><div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Margen (3,5%)</div><div className="font-heading text-2xl font-bold mt-1.5 tabular-nums text-primary">{fmtEur(totPres - totProv)}</div></Card>
          </div>

          <Card className="border-border shadow-none overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead>
                  <tr className="border-b border-border text-xs font-semibold uppercase tracking-wider text-muted-foreground bg-slate-50">
                    <th className="text-left px-3 py-3">Concepto</th>
                    <th className="text-left px-3 py-3">Estado</th>
                    <th className="text-left px-3 py-3">Finanzas</th>
                    <th className="text-right px-3 py-3">Imp. proveedor</th>
                    <th className="text-right px-3 py-3">Presupuesto</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((p) => (
                    <tr key={p.id} className="border-b border-slate-100">
                      <td className="px-3 py-2.5"><div className="font-medium text-slate-800">{p.concepto}</div>{p.proveedor_name && <div className="text-xs text-slate-400">{p.proveedor_name}</div>}</td>
                      <td className="px-3 py-2.5"><span className={cn("inline-flex px-2.5 py-1 rounded-full border text-xs font-medium", BADGE[p.estado])}>{ESTADO_LABEL[p.estado]}</span></td>
                      <td className="px-3 py-2.5">{p.finance_linked ? <span className="inline-flex items-center gap-1 text-xs font-medium text-primary"><Link2 className="h-3.5 w-3.5" /> En finanzas</span> : <span className="text-slate-400 text-xs">—</span>}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-slate-600 tabular-nums">{fmtEur(p.importe_proveedor)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-primary font-semibold tabular-nums">{fmtEur(p.importe_cliente)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
